import { Button, Modal, Space, Table, Typography as Text } from 'antd'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useReactToPrint } from 'react-to-print'

import Card from '@/components/Card'
import ReportActionButtons from '@/components/ReportActionButtons'
import ReportPrintContainer from '@/components/ReportPrintContainer'
import { pageStyle } from '@/constants'
import dimorderApi from '@/libs/api/dimorderApi'
import { currencyWithCommas } from '@/libs/numberWithCommas'
import { useSelector } from '@/redux'

import TaxReportHeader from './TaxReportHeader'
import { formatReportDateTime } from './helpers'
import useReportPagination from './useReportPagination'

/**
 * @param {Object} props
 * @param {'abb'|'full'} props.variant
 * @param {{ start: string, end: string } | null} props.params
 */
export default function TaxReportTab(props) {
  const { variant, params, onTotalsChange } = props
  const { t } = useTranslation()
  const activeMerchant = useSelector((state) => state.merchant.activeMerchant)
  const currency = activeMerchant?.setting?.currency
  const minFractionDigits = activeMerchant?.rounding?.minFractionDigits
  const isAbb = variant === 'abb'
  const printRef = useRef()
  const [printItems, setPrintItems] = useState(null)
  const [printReady, setPrintReady] = useState(false)
  const exportTitle = isAbb
    ? t('page.taxInvoiceReports.abb.exportTitle')
    : t('page.taxInvoiceReports.full.exportTitle')

  const fetchReport = (p) => {
    const api = dimorderApi.instance(activeMerchant?.id)
    return isAbb ? api?.getAbbTaxInvoiceReport(p) : api?.getFullTaxInvoiceReport(p)
  }
  const { loading, items, currentPage, hasNextPage, onNextPage, onPrevPage, count, amount } =
    useReportPagination(fetchReport, params)

  useEffect(() => {
    onTotalsChange?.({ count, amount })
  }, [count, amount, onTotalsChange])

  const money = (v) => currencyWithCommas(v, currency, minFractionDigits)
  const pageSize = params?.limit ?? 20

  const abbColumns = [
    {
      title: t('page.taxInvoiceReports.columns.date'),
      dataIndex: 'date',
      width: 180,
      render: (v) => formatReportDateTime(v),
    },
    {
      title: t('page.taxInvoiceReports.columns.number'),
      dataIndex: 'abbNumber',
      width: 240,
      render: (v) => v || '-',
    },
    {
      title: t('page.taxInvoiceReports.columns.posId'),
      dataIndex: 'posId',
      width: 140,
      render: (v) => v || '-',
    },
    {
      title: t('page.taxInvoiceReports.columns.subtotal'),
      dataIndex: 'subtotal',
      width: 140,
      align: 'right',
      render: money,
    },
    {
      title: t('page.taxInvoiceReports.columns.vat'),
      dataIndex: 'vat',
      width: 140,
      align: 'right',
      render: money,
    },
  ]

  const fullColumns = [
    {
      title: t('page.taxInvoiceReports.columns.no'),
      width: 70,
      align: 'center',
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: t('page.taxInvoiceReports.columns.date'),
      dataIndex: 'issuedAt',
      width: 180,
      render: (v) => formatReportDateTime(v),
    },
    {
      title: t('page.taxInvoiceReports.columns.posId'),
      dataIndex: 'posId',
      width: 140,
      render: (v) => v || '-',
    },
    {
      title: t('page.taxInvoiceReports.columns.documentNo'),
      dataIndex: 'fullNumber',
      width: 240,
      render: (v) => v || '-',
    },
    {
      title: t('page.taxInvoiceReports.columns.customerName'),
      dataIndex: 'customerName',
      width: 200,
      render: (v) => v || '-',
    },
    {
      title: t('page.taxInvoiceReports.columns.taxId'),
      dataIndex: 'taxId',
      width: 150,
      render: (v) => v || '-',
    },
    {
      title: t('page.taxInvoiceReports.columns.branch'),
      dataIndex: 'branch',
      width: 140,
      render: (v) => v || '-',
    },
    {
      title: t('page.taxInvoiceReports.columns.subtotal'),
      dataIndex: 'subtotal',
      width: 140,
      align: 'right',
      render: money,
    },
    {
      title: t('page.taxInvoiceReports.columns.vat'),
      dataIndex: 'vat',
      width: 140,
      align: 'right',
      render: money,
    },
    {
      title: t('page.taxInvoiceReports.columns.total'),
      dataIndex: 'total',
      width: 140,
      align: 'right',
      render: money,
    },
    {
      title: t('page.taxInvoiceReports.columns.insteadOfDocumentNo'),
      dataIndex: 'referenceNumber',
      width: 240,
      render: (v) => v || '-',
    },
  ]

  const columns = isAbb ? abbColumns : fullColumns

  const printColumns = isAbb
    ? abbColumns
    : fullColumns.map((c, i) => (i === 0 ? { ...c, render: (_, __, index) => index + 1 } : c))

  const triggerPrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle,
    onAfterPrint: () => {
      setPrintReady(false)
      setPrintItems(null)
    },
  })

  const fetchAllPages = async () => {
    const all = []
    let cursor
    for (let i = 0; i < 500; i++) {
      const res = await fetchReport({ ...params, limit: 100, cursor })
      const list = res?.invoices ?? []
      all.push(...list)
      cursor = res?.cursor
      if (!cursor || list.length === 0) break
    }
    return all
  }

  const handlePrint = async () => {
    try {
      const all = await fetchAllPages()
      setPrintItems(all)
      setPrintReady(true)
    } catch (error) {
      Modal.error({
        title: t('app.common.error'),
        content: t('app.common.submitError'),
      })
    }
  }

  useEffect(() => {
    if (printReady && printItems) {
      triggerPrint()
    }
  }, [printReady, printItems, triggerPrint])

  const handleExport = async () => {
    const api = dimorderApi.instance(activeMerchant?.id)
    try {
      await (isAbb
        ? api?.exportAbbTaxInvoiceReportCsv(params)
        : api?.exportFullTaxInvoiceReportCsv(params))
    } catch (error) {
      Modal.error({
        title: t('app.common.error'),
        content: t('app.common.exportError'),
      })
    }
  }

  return (
    <>
      <ReportActionButtons onPrint={handlePrint} onExport={handleExport} />
      <Card padding='20px' borderRadius='2px' boxShadow='1px 1px 5px rgba(0, 0, 0, 0.3)'>
        <TaxReportHeader start={params?.start} end={params?.end} />
        <Table
          loading={loading}
          rowKey={(r, idx) => r.id || r.abbNumber || r.fullNumber || idx}
          columns={columns}
          dataSource={items}
          pagination={false}
          scroll={{ x: 'max-content' }}
        />
        <Space style={{ marginTop: 12 }}>
          <Button disabled={currentPage <= 1} onClick={onPrevPage}>
            {t('app.common.previousPage')}
          </Button>
          <Text>{currentPage}</Text>
          <Button disabled={!hasNextPage} onClick={onNextPage}>
            {t('app.common.nextPage')}
          </Button>
        </Space>
      </Card>

      <ReportPrintContainer
        printRef={printRef}
        start={params?.start}
        end={params?.end}
        title={exportTitle}
      >
        <div style={{ padding: 30 }}>
          <TaxReportHeader start={params?.start} end={params?.end} />
          <Table
            rowKey={(r, idx) => r.id || r.abbNumber || r.fullNumber || idx}
            columns={printColumns}
            dataSource={printItems ?? items}
            pagination={false}
          />
        </div>
      </ReportPrintContainer>
    </>
  )
}
